import {
  arrayUnion,
  collection,
  doc,
  getDocs,
  query,
  updateDoc,
  where,
} from 'firebase/firestore';
import { db } from '../config/firebaseConfig';

/**
 * Patient visibility rules.
 *
 * `Patients` documents are stored per project, but a project is shared by many
 * employees. An employee must only ever see the patients that are actually
 * theirs, which means:
 *   1. patients they own (`Patients.ownerIds` contains their employee id), and
 *   2. patients they already booked a visit with (`Visits.patientId`).
 *
 * Ownership is a list, not a single creator: when a second employee registers a
 * patient id that already exists in the project, they are added to `ownerIds`
 * of the existing document instead of creating a duplicate.
 *
 * Visits reference the patient by its study code (`Patients.patientId`), not by
 * the Firestore document id, so every lookup below joins on that string.
 */

// Sentinel the old visit forms wrote when no patient was picked. It is still on
// existing documents, so it has to be treated as "no patient" everywhere.
const EMPTY_PATIENT_ID = '-';

// Firestore caps `in` filters; 10 values per query is safe on every version.
const IN_QUERY_LIMIT = 10;

/** Turns a stored patient id into a real id, or '' when there is none. */
export const normalizePatientId = (value) => {
  const trimmed = typeof value === 'string' ? value.trim() : '';
  return trimmed === EMPTY_PATIENT_ID ? '' : trimmed;
};

const uniquePatientIds = (values) =>
  Array.from(
    new Set((values || []).map(normalizePatientId).filter(Boolean))
  ).sort();

const chunk = (items, size) => {
  const chunks = [];
  for (let i = 0; i < items.length; i += size) {
    chunks.push(items.slice(i, i + size));
  }
  return chunks;
};

const toPatient = (patientDoc) => ({ id: patientDoc.id, ...patientDoc.data() });

/** True when the employee is one of the owners of the patient. */
export const isPatientOwner = (patient, employeeId) =>
  !!employeeId && Array.isArray(patient?.ownerIds) && patient.ownerIds.includes(employeeId);

/**
 * Patient ids the employee already booked a visit with, read from a visit list
 * that is already in memory (the personal calendar keeps one).
 */
export const getVisitedPatientIds = (visits, projectId, employeeId) => {
  if (!projectId || !employeeId) return [];

  return uniquePatientIds(
    (visits || [])
      .filter(
        (visit) =>
          visit.projectId === projectId && visit.employeeId === employeeId
      )
      .map((visit) => visit.patientId)
  );
};

/** Same as `getVisitedPatientIds`, but reads the visits from Firestore. */
export const fetchVisitedPatientIds = async (projectId, employeeId) => {
  if (!projectId || !employeeId) return [];

  const visitsSnapshot = await getDocs(
    query(
      collection(db, 'Visits'),
      where('projectId', '==', projectId),
      where('employeeId', '==', employeeId)
    )
  );

  return uniquePatientIds(
    visitsSnapshot.docs.map((visitDoc) => visitDoc.data().patientId)
  );
};

/**
 * Every patient of `projectId` registered under `patientStudyId`, whoever owns
 * it. Used by the create form to detect that the patient already exists.
 */
export const findProjectPatientsByStudyId = async (projectId, patientStudyId) => {
  const studyId = normalizePatientId(patientStudyId);
  if (!projectId || !studyId) return [];

  const snapshot = await getDocs(
    query(
      collection(db, 'Patients'),
      where('projectId', '==', projectId),
      where('patientId', '==', studyId)
    )
  );

  return snapshot.docs.map(toPatient);
};

/**
 * Adds an employee to a patient's owners. Nothing else on the document is
 * touched, so the data entered by the original owner stays as it is.
 */
export const claimPatient = async (patientDocId, employeeId) => {
  if (!patientDocId || !employeeId) return;

  await updateDoc(doc(db, 'Patients', patientDocId), {
    ownerIds: arrayUnion(employeeId),
  });
};

/**
 * The patients of `projectId` that `employeeId` is allowed to see.
 *
 * Only matching documents are requested, so another employee's patients never
 * reach the browser.
 *
 * @param {string[]} options.visitedPatientIds ids from the employee's own visits
 * @param {string[]} options.alwaysIncludeIds  ids that must stay selectable
 *                                             (e.g. the value already saved on
 *                                             the visit being edited)
 */
export const fetchAccessiblePatients = async (
  projectId,
  employeeId,
  { visitedPatientIds = [], alwaysIncludeIds = [] } = {}
) => {
  if (!projectId || !employeeId) return [];

  const wantedIds = uniquePatientIds([
    ...visitedPatientIds,
    ...alwaysIncludeIds,
  ]);

  const [ownedSnapshot, ...visitedSnapshots] = await Promise.all([
    // Patients this employee owns. A lone array-contains filter is served by the
    // automatic single-field index, so the project is narrowed below instead of
    // in the query - every document returned already belongs to the employee.
    getDocs(
      query(
        collection(db, 'Patients'),
        where('ownerIds', 'array-contains', employeeId)
      )
    ),
    // Patients this employee already has a visit with.
    ...chunk(wantedIds, IN_QUERY_LIMIT).map((ids) =>
      getDocs(
        query(
          collection(db, 'Patients'),
          where('projectId', '==', projectId),
          where('patientId', 'in', ids)
        )
      )
    ),
  ]);

  // The two sources overlap, so de-duplicate on the document id.
  const patientsById = new Map();

  ownedSnapshot.docs.forEach((patientDoc) => {
    const patient = toPatient(patientDoc);
    if (patient.projectId === projectId) {
      patientsById.set(patient.id, patient);
    }
  });

  visitedSnapshots.forEach((snapshot) => {
    snapshot.docs.forEach((patientDoc) => {
      const patient = toPatient(patientDoc);
      patientsById.set(patient.id, patient);
    });
  });

  return Array.from(patientsById.values()).sort((a, b) =>
    (a.patientId || '').localeCompare(b.patientId || '')
  );
};
